import {
  GetPatientAddressList,
  GetPatientAddressListVariables,
} from "graphql/types/GetPatientAddressList";
import React, { useEffect, useRef, useState } from "react";
import { clevertapTracking, eventTracking } from "../../clevertapTracking";
import {
  getAllProhealthAppointmentsVariables,
  getAllProhealthAppointments as getAllProhealthAppointmentssQuery,
} from "graphql/types/getAllProhealthAppointments";
import {
  getCommonPatientAttributes,
  getPatientAttributes,
  getPatientDetails,
} from "../../helpers/commonHelpers";

import CircularProgress from "@material-ui/core/CircularProgress";
import { GET_PATIENT_ADDRESSES_LIST } from "graphql/address";
import { GET_PRO_HEALTH_APPOINTMENTS } from "graphql/proHealth";
import Link from "next/link";
import { PROHEALTH_BANNER_SOURCE } from "helpers/constants";
import { SHOW_DISCOUNT } from "strings/AppConfig";
import { SignedinState } from "components/enums/SignedinState";
import _get from "lodash/get";
import { clientRoutes } from "helpers/clientRoutes";
import styles from "./ProHealthLife.module.scss";
import { useApolloClient } from "react-apollo-hooks";
import { useAuth } from "hooks/authHooks";
import { useGlobalDiagnosticsCart } from "components/Tests/DiagnosticsCartProviderGlobal";
import useIntersection from "hooks/useObserver";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { getPromotionEventsObject, isInViewport } from "helpers/gtmUtils";
import { dataLayerTracking } from "gtmTracking";

const ProHealthLife: React.FC = () => {
  const client = useApolloClient();
  const proHealthRef = useRef<HTMLDivElement>(null);
  const isRefVisible = useIntersection(proHealthRef);
  const { currentPatient, isSignedIn } = useAuth();
  const { diagnosticAddress } = useGlobalDiagnosticsCart();
  const patientDetails = getPatientDetails(currentPatient);
  const isSmallScreen = useMediaQuery("(max-width:767px)");

  const creative_name = SHOW_DISCOUNT
    ? "prohealthSliderThree.jpg"
    : isSmallScreen
    ? "ProHealthAppLanding.jpg"
    : "prohealthSliderTwo.jpg";
  const promotion_name = SHOW_DISCOUNT
    ? `${process.env.NEXT_PUBLIC_247_CDN_URL}/images/prohealth/prohealthSliderThree.jpg`
    : isSmallScreen
    ? `${process.env.NEXT_PUBLIC_247_CDN_URL}/images/banners/ProHealthAppLanding.jpg`
    : `${process.env.NEXT_PUBLIC_247_CDN_URL}/images/prohealth/prohealthSliderTwo.jpg`;

  const [
    upcomingAppointmentsCount,
    setUpcomingAppointmentsCount,
  ] = useState<number>(0);
  const [
    proHealthAppointmentsLoading,
    setProHealthAppointmentsLoading,
  ] = useState<boolean>(true);
  const [userCityName, setUserCityName] = useState<string>("");
  const [isProhealthLogged, setIsProhealthLogged] = useState<boolean>(false);

  const handleScrollable = (e) => {
    if (isInViewport(document.querySelector("#prohealth-banner"))) {
      const event = getPromotionEventsObject(
        "view_promotion",
        "Apollo247 HomePage",
        "Apollo247 HomePage",
        creative_name,
        1,
        "NA",
        promotion_name
      );
      dataLayerTracking(event);
      window.removeEventListener("scroll", handleScrollable);
    }
  };

  useEffect(() => {
    if (isInViewport(document.querySelector("#prohealth-banner"))) {
      const event = getPromotionEventsObject(
        "view_promotion",
        "Apollo247 HomePage",
        "Apollo247 HomePage",
        creative_name,
        1,
        "NA",
        promotion_name
      );
      dataLayerTracking(event);
    } else {
      window.addEventListener("scroll", handleScrollable);
    }

    return () => {
      window.removeEventListener("scroll", handleScrollable);
    };
  }, []);

  useEffect(() => {
    if (currentPatient?.id) {
      client
        .query<
          getAllProhealthAppointmentssQuery,
          getAllProhealthAppointmentsVariables
        >({
          query: GET_PRO_HEALTH_APPOINTMENTS,
          variables: {
            patientId: currentPatient ? currentPatient.id : "",
            mobileNumber: currentPatient?.mobileNumber,
          },
          fetchPolicy: "no-cache",
        })
        .then(({ data }) => {
          setUpcomingAppointmentsCount(
            data?.getAllProhealthAppointments?.upcoming?.length
          );
        })
        .catch((error) => {
          console.error(error);
          setUpcomingAppointmentsCount(0);
        })
        .finally(() => {
          setProHealthAppointmentsLoading(false);
        });

      getCurrentPatientCity().then((cityName) => setUserCityName(cityName));
    }
  }, [currentPatient]);

  const getCurrentPatientCity = async () => {
    try {
      const { data } = await client.query<
        GetPatientAddressList,
        GetPatientAddressListVariables
      >({
        query: GET_PATIENT_ADDRESSES_LIST,
        variables: {
          patientId: currentPatient?.id,
        },
        fetchPolicy: "no-cache",
      });
      if (data?.getPatientAddressList?.addressList?.length) {
        const allAddresses = data.getPatientAddressList.addressList.reverse();
        const addressToUse =
          allAddresses?.find((address) => address?.defaultAddress) ||
          allAddresses[0];
        return addressToUse?.city || "Not Available";
      } else {
        return "Not Available";
      }
    } catch (e) {
      console.error("Error occured while fetching patient addresses", e);
      return "Not Available";
    }
  };

  useEffect(() => {
    if (
      isRefVisible &&
      isSignedIn &&
      isSignedIn !== SignedinState.UNRESOLVED &&
      !isProhealthLogged
    ) {
      const clevertapData = {
        ...getCommonPatientAttributes(currentPatient),
        "Patient city": diagnosticAddress?.cityName,
        "Banner source": PROHEALTH_BANNER_SOURCE.HOME_MAIN_BANNER,
        "Banner Position": 1,
        City: diagnosticAddress?.cityName || "Not available",
        CityId: diagnosticAddress?.cityID || "Not available",
      };
      clevertapTracking("Prohealth banner viewed", clevertapData);
      setIsProhealthLogged(true);
    }
  }, [isRefVisible, isSignedIn, isProhealthLogged]);

  const handleClickApolloProHealthBanner = () => {
    const eventData = getPromotionEventsObject(
      "select_promotion",
      "Apollo247 HomePage",
      "Apollo247 HomePage",
      creative_name,
      1,
      "NA",
      promotion_name
    );
    dataLayerTracking(eventData);
  };

  return (
    <div className={styles.proHealthContainer} ref={proHealthRef}>
      {upcomingAppointmentsCount > 0 && (
        <div className={styles.proHealthAppointmentsCount}>
          <Link
            href={`${clientRoutes.apolloProHealthAppointments()}?utm_mobile_number=${
              currentPatient?.mobileNumber
            }`}
            prefetch={false}
          >
            <a>
              <div
                className={styles.card}
                title={"View active prohealth appointments"}
              >
                <p>
                  ProHealth Appointments
                  <div className={styles.totalConsults}>
                    {proHealthAppointmentsLoading ? (
                      <CircularProgress size={10} />
                    ) : (
                      <>
                        <span className={styles.greenCircle} />
                        <span>
                          {upcomingAppointmentsCount} Upcoming Appointment
                          {upcomingAppointmentsCount > 1 && "s"}
                        </span>
                      </>
                    )}
                  </div>
                </p>
                <span className={styles.rightArrow}>
                  <span className="icon-ic_arrow_right" />
                </span>
              </div>
            </a>
          </Link>
        </div>
      )}
      <div
        onClick={handleClickApolloProHealthBanner}
        className={styles.proHealthBanner}
        id="prohealth-banner"
      >
        <Link
          href={`${clientRoutes.apolloProHealthCategories("homepage-banner")}`}
          prefetch={false}
        >
          <a
            onClick={() => {
              localStorage?.removeItem("proHealthSelectedHospitalId");
              localStorage?.removeItem("proHealthSelectedHospitalName");
              localStorage?.removeItem("proHealthSelectedCityName");
              localStorage?.removeItem("proHealthSelectedHospitalUnitType");
              eventTracking("Pro Health Banner Clicked", {
                ...getPatientAttributes(patientDetails),
                City: userCityName,
                Nav_src: "Home page",
              });
            }}
          >
            <img
              src={
                SHOW_DISCOUNT
                  ? `${process.env.NEXT_PUBLIC_247_CDN_URL}/images/prohealth/prohealthSliderThree.jpg`
                  : isSmallScreen
                  ? `${process.env.NEXT_PUBLIC_247_CDN_URL}/images/banners/ProHealthAppLanding.jpg`
                  : `${process.env.NEXT_PUBLIC_247_CDN_URL}/images/prohealth/prohealthSliderTwo.jpg`
              }
              aria-label="Apollo-ProHealthLife"
              width="100%"
            />
          </a>
        </Link>
      </div>
    </div>
  );
};

export default ProHealthLife;
